import { ReactNode } from 'react';
import { CitationResponse, DocumentResponse } from 'types/api-responses';
import { Date } from 'components/date';
import { snakeToCapitalCase } from 'components/json-renderer/utils';
import { CountyName } from './county-name';
import { EMPTY_CELL } from 'utils/empty-cell';

export const getCitationValue = (
  citations: CitationResponse[],
  field: string
): string | undefined =>
  citations?.find((c) => c.provisionField === field)?.provisionValue;

export type DocumentField = {
  label: string;
  render: (document: DocumentResponse) => ReactNode;
};

const formatRegulatoryBody = (document: DocumentResponse): string => {
  const regulatoryBody = document.regulatoryBody;
  if (!regulatoryBody) return EMPTY_CELL;
  return `${regulatoryBody.name} (${
    regulatoryBody.abbreviation || regulatoryBody.state.abbreviation
  })`;
};

const DOCUMENT_FIELDS: Record<string, DocumentField[]> = {
  'Document::Courthouse': [
    {
      label: 'County',
      render: (doc) => <CountyName county={doc.county} />,
    },
    {
      label: 'Book Type',
      render: (doc) => doc.bookType || EMPTY_CELL,
    },
    {
      label: 'Instrument Number',
      render: (doc) => doc.instrumentNumber || 'Unknown',
    },
    {
      label: 'Document Type',
      render: (doc) => doc.documentType || EMPTY_CELL,
    },
    {
      label: 'Effective Date',
      render: (doc) => <Date date={doc.effectiveDate} fallback={EMPTY_CELL} />,
    },
    {
      label: 'Instrument Date',
      render: (doc) => <Date date={doc.instrumentDate} fallback={EMPTY_CELL} />,
    },
    {
      label: 'File Date',
      render: (doc) => <Date date={doc.fileDate} fallback={EMPTY_CELL} />,
    },
    {
      label: 'Recorded Date',
      render: (doc) => <Date date={doc.recordedDate} fallback={EMPTY_CELL} />,
    },
  ],
  'Document::Regulatory': [
    {
      label: 'Regulatory Body',
      render: (doc) => formatRegulatoryBody(doc),
    },
    {
      label: 'Document Type',
      render: (doc) => {
        return doc.documentType
          ? snakeToCapitalCase(doc.documentType)
          : EMPTY_CELL;
      },
    },
    {
      label: 'Filing Number',
      render: (doc) =>
        getCitationValue(doc.citations, 'filing_number') || EMPTY_CELL,
    },
    {
      label: 'Filing State',
      render: (doc) =>
        getCitationValue(doc.citations, 'state_name') || EMPTY_CELL,
    },
    {
      label: 'Filing Date',
      render: (doc) => {
        if (doc.fileDate) {
          return (
            <>
              <Date date={doc.fileDate} />
              {doc.effectiveDate && (
                <>
                  {' '}
                  (Effective <Date date={doc.effectiveDate} />)
                </>
              )}
            </>
          );
        }
        return EMPTY_CELL;
      },
    },
    {
      label: 'Agency District',
      render: (doc) =>
        getCitationValue(doc.citations, 'agency_district') || EMPTY_CELL,
    },
    {
      label: 'Expiration Date',
      render: (doc) => {
        const value = getCitationValue(doc.citations, 'expiration_date');
        return value ? <Date date={value} fallback={EMPTY_CELL} /> : EMPTY_CELL;
      },
    },
    {
      label: 'Status',
      render: (doc) => getCitationValue(doc.citations, 'status') || EMPTY_CELL,
    },
  ],
  'Document::Afe': [
    {
      label: 'Operator',
      render: (doc) =>
        getCitationValue(doc.citations, 'operator_code') || EMPTY_CELL,
    },
    {
      label: 'AFE Number',
      render: (doc) =>
        doc.instrumentNumber ||
        getCitationValue(doc.citations, 'afe_number') ||
        EMPTY_CELL,
    },
    {
      label: 'AFE Type',
      render: (doc) =>
        doc.documentType ||
        getCitationValue(doc.citations, 'afe_type') ||
        EMPTY_CELL,
    },
    {
      label: 'Effective Date',
      render: (doc) => <Date date={doc.effectiveDate} fallback={EMPTY_CELL} />,
    },
    {
      label: 'Date Received',
      render: (doc) => {
        const value = getCitationValue(doc.citations, 'received_date');
        return value ? <Date date={value} fallback={EMPTY_CELL} /> : EMPTY_CELL;
      },
    },
    {
      label: 'Status',
      render: (doc) =>
        getCitationValue(doc.citations, 'revision_status') || EMPTY_CELL,
    },
    {
      label: 'County',
      render: (doc) => <CountyName county={doc.county} />,
    },
  ],
  'Document::DivisionOrder': [
    {
      label: 'Operator',
      render: (doc) =>
        getCitationValue(doc.citations, 'operator_name') || EMPTY_CELL,
    },
    {
      label: 'Document Type',
      render: (doc) => {
        return doc.documentType
          ? snakeToCapitalCase(doc.documentType)
          : EMPTY_CELL;
      },
    },
    {
      label: 'DO Number',
      render: (doc) =>
        doc.instrumentNumber ||
        getCitationValue(doc.citations, 'division_order_number') ||
        EMPTY_CELL,
    },
    {
      label: 'Effective Date',
      render: (doc) => <Date date={doc.effectiveDate} fallback={EMPTY_CELL} />,
    },
    {
      label: 'Date Received',
      render: (doc) => {
        const value = getCitationValue(
          doc.citations,
          'date_received_from_operator'
        );
        return value ? <Date date={value} fallback={EMPTY_CELL} /> : EMPTY_CELL;
      },
    },
    {
      label: 'Status',
      render: (doc) => getCitationValue(doc.citations, 'status') || EMPTY_CELL,
    },
    {
      label: 'County',
      render: (doc) => <CountyName county={doc.county} />,
    },
  ],
};

export const getDocumentFields = (
  document: DocumentResponse
): DocumentField[] =>
  DOCUMENT_FIELDS[document.type || ''] ||
  DOCUMENT_FIELDS['Document::Courthouse'];
